import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getBlogs, updateBlogStatus } from '../../../services/admin/blogService';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { formatDate, formatRelativeTime, formatDateWithRelative } from '../../../utils/dateFormatter';

const BlogsList = () => {
  const { user } = useAdminAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('');
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  
  const limit = 10; // Max records per page

  // Fetch blogs with filters
  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = {
        page: pagination.page,
        limit
      };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (featuredFilter === 'featured') params.isFeatured = true;
      if (featuredFilter === 'regular') params.isFeatured = false;
      
      const response = await getBlogs(params);
      
      if (response.success) {
        setBlogs(response.data || []);
        setPagination(response.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 1
        });
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, featuredFilter, pagination.page]);

  // Fetch blogs when filters change
  useEffect(() => {
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchTerm, statusFilter, featuredFilter]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    switch (filterName) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'featured':
        setFeaturedFilter(value);
        break;
      default:
        break;
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.pages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  // Handle blog status update
  const handleStatusUpdate = async (blogId, currentStatus) => {
    if (updatingStatus === blogId) return;
    
    try {
      setUpdatingStatus(blogId);
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      const response = await updateBlogStatus(blogId, newStatus);
      
      if (response.success) {
        // Update the blog in the local state
        setBlogs(blogs.map(blog => 
          blog._id === blogId ? { ...blog, status: newStatus } : blog
        ));
      } else {
        alert(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating blog status:', error);
      alert(error.response?.data?.message || 'Error updating blog status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle blog deletion
  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      try {
        // Note: You'll need to implement deleteBlog in blogService
        // const response = await deleteBlog(blogId);
        // if (response.success) {
        //   fetchBlogs(); // Refresh the list
        // }
        alert(`Blog deletion functionality needs to be implemented in the service.`);
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-yellow-100 text-yellow-800', label: 'Inactive' },
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const getFeaturedBadge = (isFeatured) => {
    return isFeatured ? (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <i className="fas fa-star mr-1"></i> Featured
      </span>
    ) : null;
  };

  // Calculate read time from full description
  const calculateReadTime = (content) => {
    if (!content) return '2 min read';
    
    // Remove HTML tags and count words
    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / 200); // 200 words per minute
    
    return `${readTime || 1} min read`;
  };

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const { page, pages } = pagination;
    
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      items.push(i);
    }
    
    return items;
  };

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Blogs Management | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Blog Management</h2>
            <p className="text-gray-400">
              {loading ? 'Loading blogs...' : `Total ${pagination.total} blogs found`}
            </p>
          </div>
          <Link
            to="/hpanel/blogs/add"
            className="btn-primary text-white font-semibold py-2 px-4 rounded-lg flex items-center"
          >
            <i className="fas fa-plus mr-2"></i>
            Write New Blog
          </Link>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search blogs by title or description..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>              
            </select>
          </div>

          {/* Featured Filter */}
          {/* <div>
            <select
              value={featuredFilter}
              onChange={(e) => handleFilterChange('featured', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Posts</option>
              <option value="featured">Featured</option>
              <option value="regular">Regular</option>
            </select>
          </div> */}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading blogs...</p>
        </div>
      )}

      {/* Blogs Table */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Blog Post
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blogs.map((blog) => (
                  <tr key={blog._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-12 w-16 flex-shrink-0">
                          {blog.featuredImage ? (
                            <img
                              className="h-12 w-16 rounded-lg object-cover"
                              src={blog.featuredImage}
                              alt={blog.title}
                              onError={(e) => {
                                e.target.src = '/HunarPool.png';
                                e.target.onerror = null;
                              }}
                            />
                          ) : (
                            <div className="h-12 w-16 rounded-lg bg-gray-200 flex items-center justify-center">
                              <i className="fas fa-image text-gray-400"></i>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900 line-clamp-1">
                              {blog.title}
                            </div>
                            {getFeaturedBadge(blog.isFeatured)}
                          </div>
                          {/* <div className="text-sm text-gray-500 line-clamp-2 mt-1">
                            {blog.description}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {calculateReadTime(blog.fullDescription)} • 
                            {blog.tags && blog.tags.length > 0 && (
                              <span className="ml-1">
                                Tags: {blog.tags.slice(0, 2).join(', ')}
                                {blog.tags.length > 2 && '...'}
                              </span>
                            )}
                          </div> */}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(blog.status)}
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{blog.createdBy?.email || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">
                          {blog.updatedBy ? 'Updated by: ' + blog.updatedBy.email : 'Not updated'}
                        </div>
                      </div>
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div>Created: {formatDate(blog.createdAt, "datetime")}</div>
                        <div>Updated: {formatDate(blog.updatedAt, "datetime")}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-3">
                        <Link
                          to={`/hpanel/blogs/edit/${blog._id}`}
                          className="text-green-600 hover:text-green-900"
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button 
                          className="text-blue-600 hover:text-blue-900"
                          title="Preview"
                          onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          onClick={() => handleStatusUpdate(blog._id, blog.status)}
                          disabled={updatingStatus === blog._id}
                          className={`${
                            blog.status === 'active' 
                              ? 'text-orange-600 hover:text-orange-900' 
                              : 'text-blue-600 hover:text-blue-900'
                          } ${updatingStatus === blog._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={blog.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {updatingStatus === blog._id ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : blog.status === 'active' ? (
                            <i className="fas fa-pause"></i>
                          ) : (
                            <i className="fas fa-play"></i>
                          )}
                        </button>
                        {/* <button 
                          onClick={() => handleDeleteBlog(blog._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {blogs.length === 0 && !loading && (
            <div className="text-center py-12">
              <i className="fas fa-blog text-4xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No blogs found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search criteria or create a new blog post.</p>
              <Link
                to="/hpanel/blogs/add"
                className="btn-primary text-white font-semibold py-2 px-6 rounded-lg inline-flex items-center"
              >
                <i className="fas fa-plus mr-2"></i>
                Write New Blog
              </Link>
            </div>
          )}

          {/* Pagination */}
          {blogs.length > 0 && pagination.pages > 1 && (
            <div className="bg-white px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">
                    {(pagination.page - 1) * limit + 1}
                  </span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * limit, pagination.total)}
                  </span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      pagination.page === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {generatePaginationItems().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 border rounded-md text-sm font-medium ${
                        pageNum === pagination.page
                          ? 'bg-primary-600 text-gray-800 border-primary-600'
                          : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      pagination.page === pagination.pages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info when there are blogs but only one page */}
          {blogs.length > 0 && pagination.pages === 1 && (
            <div className="bg-white px-6 py-3 border-t border-gray-200">
              <div className="text-sm text-gray-700 text-center">
                Showing all {pagination.total} blogs
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogsList;